import { useState, useEffect, useRef, useCallback } from 'react';
import { DEMO_STEPS } from '../components/Demo';
import { ANIMAL_OPTIONS } from '../constants';
import { preloadAnimalSound } from '../audio';

// First-ever-launch interactive demo. demoStep = -1 means not running; the
// 'tap'/'rhythm' steps advance as the user taps the orbs (via handleDemoTap),
// the Stats step advances when the Stats screen is closed, and the final Pro
// introduction offers a choice between Free Tap and more information.
//
// The demo is non-destructive: it snapshots the user's real settings on entry
// and restores them when it ends, so any changes made during the tour (colour,
// size, kids…) revert and the orbs return to how they started.
//
// - `demoStepRef`: owned by the caller (shared with stats + taps); kept in sync
//   with the step state here.
// - `onFinish(outcome)`: caller-side cleanup + navigation when the demo
//   ends/skips. `outcome` is `free-tap` only for the final Free Tap action.
const useDemo = ({ demoStepRef, settingsRef, setSettings, defaultSettings, onFinish }) => {
  const [demoStep, setDemoStep] = useState(-1);
  useEffect(() => { demoStepRef.current = demoStep; }, [demoStep, demoStepRef]);
  const demoTapsRef = useRef({ step: -1, count: 0, sides: new Set(), lastSide: null });

  const startDemo = useCallback(() => {
    demoTapsRef.current = { step: -1, count: 0, sides: new Set(), lastSide: null };
    setDemoStep(0);
    // The 'kids' step is several steps away — decode every animal recording
    // now so it's already cached by the time the user flips the switch,
    // instead of the first play falling back to the synth while it loads.
    Object.keys(ANIMAL_OPTIONS).forEach((key) => key !== 'random' && preloadAnimalSound(key));
  }, []);

  const finishDemo = useCallback((outcome = 'home') => {
    setDemoStep(-1);
    try { localStorage.setItem('taptap_demoSeen', '1'); } catch { /* ignore */ }
    onFinish(outcome);
  }, [onFinish]);

  const advanceDemo = useCallback(() => {
    const next = demoStepRef.current + 1;
    if (next >= DEMO_STEPS.length) finishDemo();
    else setDemoStep(next);
  }, [demoStepRef, finishDemo]);

  // Snapshot the user's real settings on entry and restore them on exit. Local
  // state only during the demo (no Firestore write) — the caller passes a
  // local-only settings updater to the panel while the demo runs.
  const demoActiveRef = useRef(false);
  const origSettingsRef = useRef(null);
  useEffect(() => {
    if (demoStep < 0) {
      if (demoActiveRef.current) {
        demoActiveRef.current = false;
        if (origSettingsRef.current) setSettings(origSettingsRef.current);
      }
      return;
    }
    if (!demoActiveRef.current) {
      origSettingsRef.current = { ...(settingsRef.current || defaultSettings) };
      demoActiveRef.current = true;
    }
    // Keep the Free tour on standard orbs even if a previous local state was
    // displaying Kids Mode.
    setSettings((s) => (s.kidsMode ? { ...s, kidsMode: false } : s));
  }, [demoStep, setSettings, settingsRef, defaultSettings]);

  // 'admire' step: a short, silent pause on the orb screen (tappable) so the
  // user enjoys the settings they just chose, then we move on automatically.
  useEffect(() => {
    if (DEMO_STEPS[demoStep]?.key !== 'admire') return;
    const id = setTimeout(advanceDemo, 2000);
    return () => clearTimeout(id);
  }, [demoStep, advanceDemo]);

  // Validate the demonstration, not the ongoing tapping mechanic. Users can
  // still skip at any point; ordinary sessions remain freely tappable.
  const handleDemoTap = useCallback((orbNumber) => {
    const step = demoStepRef.current;
    const key = DEMO_STEPS[step]?.key;
    if (!['tap', 'rhythm', 'kids'].includes(key)) return;
    if (key === 'kids' && !settingsRef.current?.kidsMode) return;
    if (demoTapsRef.current.step !== step) {
      demoTapsRef.current = { step, count: 0, sides: new Set(), lastSide: null };
    }
    const tally = demoTapsRef.current;
    tally.sides.add(orbNumber);
    if (key !== 'rhythm' || tally.lastSide !== orbNumber) tally.count += 1;
    tally.lastSide = orbNumber;
    const complete = key === 'tap' ? tally.sides.size === 2 : tally.count >= 6;
    if (complete) setDemoStep(step + 1);
  }, [demoStepRef, settingsRef]);

  return { demoStep, startDemo, advanceDemo, finishDemo, handleDemoTap };
};

export default useDemo;
